import { useState, useEffect } from 'react'
import { Outlet, NavLink, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  TrendingUp,
  Play,
  Target,
  History,
  BarChart3,
  Trophy,
  Users,
  Menu,
  X,
  Activity,
  ChevronDown,
  Wrench,
} from 'lucide-react'
import { Logo, LogoMark } from './Logo'
import { AudienceProvider, useAudience } from './AudienceToggle'
import { SportProvider, useSport, SPORTS, SportIcon } from '../contexts/SportContext'

// Primary nav items (always visible)
const primaryNavItems = [
  { to: '/', icon: TrendingUp, label: 'Dashboard' },
  { to: '/live', icon: Activity, label: 'Live' },
  { to: '/predictions', icon: Target, label: 'Predictions' },
]

// Analysis dropdown items
const analysisItems = [
  { to: '/analytics', icon: BarChart3, label: 'Track Record' },
  { to: '/leaderboards', icon: Trophy, label: 'Rankings' },
  { to: '/players', icon: Users, label: 'Players' },
]

// Tools dropdown items
const toolsItems = [
  { to: '/tools', icon: Wrench, label: 'All Tools' },
  { to: '/tools#parlay', icon: Target, label: 'Parlay Calculator' },
  { to: '/tools#injury', icon: Activity, label: 'Injury Impact' },
]

// All items for mobile
const navItems = [...primaryNavItems, ...analysisItems, { to: '/tools', icon: Wrench, label: 'Tools' }]

function SportSelector() {
  const { sport, setSport } = useSport()

  return (
    <div className="flex items-center bg-dark-800/80 rounded-xl p-0.5 sm:p-1 border border-dark-700/50">
      {Object.values(SPORTS).map((s) => (
        <button
          key={s.id}
          onClick={() => setSport(s.id)}
          className={`relative flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all ${
            sport === s.id
              ? 'text-white'
              : 'text-dark-400 hover:text-dark-200'
          }`}
        >
          {sport === s.id && (
            <motion.div
              layoutId="sportIndicator"
              className="absolute inset-0 bg-gradient-to-r from-accent-cyan to-accent-purple rounded-lg"
              transition={{ type: 'spring', bounce: 0.2, duration: 0.4 }}
            />
          )}
          <span className="relative flex items-center gap-1 sm:gap-2">
            <SportIcon sport={s.id} className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">{s.name}</span>
          </span>
        </button>
      ))}
    </div>
  )
}

function ModeToggle() {
  const { audience, setAudience } = useAudience()

  const modes = [
    { id: 'casual', label: 'Simple' },
    { id: 'sports_fanatic', label: 'Fan' },
    { id: 'data_scientist', label: 'Pro' },
  ]

  return (
    <div className="flex items-center bg-dark-800/80 rounded-xl p-1 border border-dark-700/50">
      {modes.map((mode) => (
        <button
          key={mode.id}
          onClick={() => setAudience(mode.id)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
            audience === mode.id
              ? 'bg-gradient-to-r from-accent-cyan to-accent-purple text-white shadow-lg'
              : 'text-dark-400 hover:text-dark-200'
          }`}
        >
          {mode.label}
        </button>
      ))}
    </div>
  )
}

function NavDropdown({ label, icon: Icon, items, isActive, isOpen, onToggle, onClose }) {
  const location = useLocation()

  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
          isActive || isOpen
            ? 'bg-gradient-to-r from-accent-cyan/20 to-accent-purple/20 text-white shadow-sm border border-accent-cyan/20'
            : 'text-dark-400 hover:text-white hover:bg-dark-800/50'
        }`}
      >
        <Icon className={`w-4 h-4 ${isActive ? 'text-accent-cyan' : ''}`} />
        <span>{label}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 mt-2 w-52 bg-dark-900/95 backdrop-blur-xl border border-dark-700/50 rounded-xl shadow-xl shadow-black/30 overflow-hidden z-50"
          >
            {items.map((item) => {
              const itemPath = item.to.split('#')[0]
              const isItemActive = location.pathname === itemPath

              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={onClose}
                  className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all ${
                    isItemActive
                      ? 'bg-accent-cyan/10 text-accent-cyan'
                      : 'text-dark-300 hover:bg-dark-800 hover:text-white'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </NavLink>
              )
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function DesktopNav() {
  const [openDropdown, setOpenDropdown] = useState(null) // 'analysis' | 'tools' | null
  const location = useLocation()

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.nav-dropdown')) {
        setOpenDropdown(null)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  // Close dropdown on route change
  useEffect(() => {
    setOpenDropdown(null)
  }, [location.pathname])

  // Check if any analysis item is active
  const isAnalysisActive = analysisItems.some(
    item => location.pathname === item.to.split('#')[0]
  )

  // Check if any tools item is active
  const isToolsActive = toolsItems.some(
    item => location.pathname === item.to.split('#')[0]
  )

  return (
    <nav className="hidden lg:flex items-center justify-between px-6 py-4 border-b border-dark-800/50 bg-dark-950/80 backdrop-blur-xl sticky top-0 z-50">
      {/* Left - Logo & Sport */}
      <div className="flex items-center gap-6 mr-8">
        <NavLink to="/" className="flex items-center gap-3 group">
          <div className="w-10 h-10 rounded-xl bg-dark-900 border border-dark-700/50 flex items-center justify-center shadow-lg shadow-accent-cyan/10 group-hover:shadow-accent-cyan/30 group-hover:border-accent-cyan/30 transition-all">
            <Logo size={32} />
          </div>
          <div>
            <h1 className="font-bold text-lg text-white tracking-tight whitespace-nowrap">Game Decoded</h1>
            <p className="text-xs text-dark-500 -mt-0.5">Outsmart the Odds</p>
          </div>
        </NavLink>

        <div className="w-px h-8 bg-dark-800" />

        <SportSelector />
      </div>

      {/* Center - Navigation */}
      <div className="flex items-center gap-1 bg-dark-900/50 rounded-2xl p-1.5 border border-dark-800/50">
        {primaryNavItems.map((item) => {
          const isActive = location.pathname === item.to ||
            (item.to !== '/' && location.pathname.startsWith(item.to))

          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setOpenDropdown(null)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                isActive
                  ? 'bg-gradient-to-r from-accent-cyan/20 to-accent-purple/20 text-white shadow-sm border border-accent-cyan/20'
                  : 'text-dark-400 hover:text-white hover:bg-dark-800/50'
              }`}
            >
              <item.icon className={`w-4 h-4 ${isActive ? 'text-accent-cyan' : ''}`} />
              <span>{item.label}</span>
            </NavLink>
          )
        })}

        {/* Analysis Dropdown */}
        <div className="nav-dropdown">
          <NavDropdown
            label="Analysis"
            icon={BarChart3}
            items={analysisItems}
            isActive={isAnalysisActive}
            isOpen={openDropdown === 'analysis'}
            onToggle={() => setOpenDropdown(openDropdown === 'analysis' ? null : 'analysis')}
            onClose={() => setOpenDropdown(null)}
          />
        </div>

        {/* Tools Dropdown */}
        <div className="nav-dropdown">
          <NavDropdown
            label="Tools"
            icon={Wrench}
            items={toolsItems}
            isActive={isToolsActive}
            isOpen={openDropdown === 'tools'}
            onToggle={() => setOpenDropdown(openDropdown === 'tools' ? null : 'tools')}
            onClose={() => setOpenDropdown(null)}
          />
        </div>
      </div>

      {/* Right - Mode Toggle */}
      <ModeToggle />
    </nav>
  )
}

function MobileNav() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    setIsMenuOpen(false)
  }, [location])

  return (
    <>
      {/* Mobile Header */}
      <nav className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-dark-950/95 backdrop-blur-xl border-b border-dark-800/50">
        <div className="flex items-center justify-between px-4 py-3">
          <NavLink to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-dark-900 border border-dark-700/50 flex items-center justify-center">
              <LogoMark size={24} />
            </div>
            <span className="font-bold text-white text-sm sm:text-base">Game Decoded</span>
          </NavLink>

          <div className="flex items-center gap-3">
            <SportSelector />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="w-10 h-10 rounded-xl bg-dark-800 flex items-center justify-center text-dark-300 hover:text-white transition-colors"
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="border-t border-dark-800/50 bg-dark-950"
            >
              <div className="p-4 space-y-2">
                {navItems.map((item) => {
                  const isActive = location.pathname === item.to
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                        isActive
                          ? 'bg-gradient-to-r from-accent-cyan/20 to-accent-purple/20 text-white'
                          : 'text-dark-400 hover:bg-dark-800 hover:text-white'
                      }`}
                    >
                      <item.icon className={`w-5 h-5 ${isActive ? 'text-accent-cyan' : ''}`} />
                      <span className="font-medium">{item.label}</span>
                    </NavLink>
                  )
                })}

                <div className="pt-4 border-t border-dark-800 mt-4">
                  <p className="text-xs text-dark-500 mb-3 px-4">Display Mode</p>
                  <ModeToggle />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Mobile Bottom Tab Bar */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-dark-950/95 backdrop-blur-xl border-t border-dark-800/50 safe-area-inset">
        <div className="flex items-center justify-around px-2 py-2">
          {navItems.slice(0, 5).map((item) => {
            const isActive = location.pathname === item.to
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={`flex flex-col items-center gap-1 px-3 py-2 rounded-xl min-w-[60px] transition-all ${
                  isActive ? 'text-accent-cyan' : 'text-dark-500'
                }`}
              >
                <item.icon className={`w-5 h-5 ${isActive ? 'text-accent-cyan' : ''}`} />
                <span className="text-[10px] font-medium">{item.label.split(' ')[0]}</span>
              </NavLink>
            )
          })}
        </div>
      </nav>
    </>
  )
}

function PublicLayout() {
  return (
    <SportProvider>
      <AudienceProvider>
        <div className="min-h-screen bg-dark-950">
          {/* Ambient background effects */}
          <div className="fixed inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 left-1/4 w-[800px] h-[800px] bg-accent-cyan/5 rounded-full blur-[120px]" />
            <div className="absolute bottom-0 right-1/4 w-[800px] h-[800px] bg-accent-purple/5 rounded-full blur-[120px]" />
          </div>

          {/* Navigation */}
          <DesktopNav />
          <MobileNav />

          {/* Main Content */}
          <main className="relative z-10 pt-4 lg:pt-8 pb-24 lg:pb-8 px-4 lg:px-8 max-w-7xl mx-auto min-h-[calc(100vh-80px)]">
            {/* Mobile top padding for fixed header */}
            <div className="lg:hidden h-16" />

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Outlet />
            </motion.div>
          </main>

          {/* Footer */}
          <footer className="relative z-10 border-t border-dark-800/50 bg-dark-950/80">
            {/* Main footer content */}
            <div className="max-w-7xl mx-auto px-8 py-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Brand & About */}
                <div className="lg:col-span-2 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-dark-900 border border-dark-700/50 flex items-center justify-center">
                      <LogoMark size={24} />
                    </div>
                    <span className="font-bold text-dark-100">Game Decoded</span>
                  </div>
                  <p className="text-sm text-dark-400 max-w-md leading-relaxed">
                    Sports predictions built by data scientists, not sports fanatics. We apply decision science,
                    economics, and statistical modeling to decode game outcomes. You don't need to know the
                    players - the numbers tell the story.
                  </p>
                  <div className="flex items-center gap-4 pt-2">
                    <a
                      href="https://truthsocial.com/@fractalsaboteur"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-dark-400 hover:text-accent-cyan transition-colors flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                      </svg>
                      @fractalsaboteur
                    </a>
                  </div>
                </div>

                {/* Quick Links */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-dark-300 uppercase tracking-wider">Quick Links</h4>
                  <div className="space-y-2">
                    <NavLink to="/predictions" className="block text-sm text-dark-400 hover:text-accent-cyan transition-colors">
                      Today's Predictions
                    </NavLink>
                    <NavLink to="/live" className="block text-sm text-dark-400 hover:text-accent-cyan transition-colors">
                      Live
                    </NavLink>
                    <NavLink to="/analytics" className="block text-sm text-dark-400 hover:text-accent-cyan transition-colors">
                      Track Record
                    </NavLink>
                    <NavLink to="/leaderboards" className="block text-sm text-dark-400 hover:text-accent-cyan transition-colors">
                      Team Rankings
                    </NavLink>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom bar */}
            <div className="border-t border-dark-800/50 px-4 sm:px-8 py-4">
              <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-dark-500">
                <p>&copy; {new Date().getFullYear()} Game Decoded. Predictions for entertainment only.</p>
                <div className="flex items-center gap-4 sm:gap-6 flex-wrap justify-center">
                  <span>21+ | Gambling Problem? 1-800-GAMBLER</span>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </AudienceProvider>
    </SportProvider>
  )
}

export default PublicLayout
